import React, { useState } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { SmartCaptcha } from '@yandex/smart-captcha';
import { Btn, H4, P } from '../../../AbstractElements';
import { EmailAddress, Password } from '../../../Constant';
import { useNavigate, useSearchParams } from 'react-router-dom';

const getApiBase = () => {
  const base = process.env.REACT_APP_FW_API;
  if (!base) return '';
  return base.replace(/\/$/, '');
};

const CAPTCHA_KEY = process.env.REACT_APP_YANDEX_CAPCHA_KEY || '';

const initialErrors = {
  email: '',
  password: '',
  passwordConfirm: '',
  captcha: '',
  code: '',
  common: '',
};

const RegisterTab = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [code, setCode] = useState('');
  const [togglePassword, setTogglePassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [fieldErrors, setFieldErrors] = useState(initialErrors);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [registrationId, setRegistrationId] = useState(null);
  const [searchParams] = useSearchParams();
  const requestLkId = searchParams.get('id');
  const navigate = useNavigate();

  const clearAllErrors = () => setFieldErrors(initialErrors);
  const clearFieldError = (name) => {
    setFieldErrors((prev) => ({ ...prev, [name]: '', common: '' }));
  };

  const applyApiErrors = (data, fieldsForStage) => {
    const next = { ...initialErrors };
    if (data && typeof data === 'object') {
      const payload = data.data && typeof data.data === 'object' ? data.data : data;
      fieldsForStage.forEach((field) => {
        let apiField = field;
        if (field === 'captcha') {
          apiField = payload.smart_token ? 'smart_token' : 'captcha';
        }
        if (Array.isArray(payload[apiField]) && payload[apiField].length > 0) {
          next[field] = payload[apiField][0];
        }
      });
      const message = data.message || payload.message || data.error || 'Ошибка валидации.';
      if (!fieldsForStage.some((f) => next[f])) {
        next[fieldsForStage[0]] = typeof message === 'string' ? message : 'Ошибка валидации.';
      }
    } else {
      next[fieldsForStage[0]] = 'Ошибка валидации.';
    }
    setFieldErrors(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 'form') {
      await handleFirstStep();
    } else {
      await handleSecondStep();
    }
  };

  const handleFirstStep = async () => {
    clearAllErrors();
    const trimmedEmail = email.trim();
    const errors = { ...initialErrors };

    if (!trimmedEmail) errors.email = 'Введите email.';
    if (!password) errors.password = 'Введите пароль.';
    if (!passwordConfirm) errors.passwordConfirm = 'Повторите пароль.';
    else if (passwordConfirm !== password) errors.passwordConfirm = 'Пароли не совпадают.';
    if (!captchaToken) errors.captcha = 'Подтвердите, что вы не робот.';

    if (errors.email || errors.password || errors.passwordConfirm || errors.captcha) {
      setFieldErrors(errors);
      return;
    }

    const base = getApiBase();
    if (!base) {
      setFieldErrors({ ...initialErrors, email: 'Не задан REACT_APP_FW_API.' });
      return;
    }

    setLoading(true);
    try {
      if (requestLkId) {
        const res = await fetch(`${base}/v1/birthday/registration-ajax`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: trimmedEmail,
            password,
            smart_token: captchaToken,
            id: Number(requestLkId),
          }),
        });
        const data = await res.json().catch(() => ({}));
        const token = data.access_token_180 || data.access_token;

        if (res.ok && token) {
          localStorage.setItem('token', token);
          localStorage.setItem('login', JSON.stringify(true));
          navigate(`${process.env.PUBLIC_URL}/grant`);
          return;
        }

        if (res.status === 400) {
          applyApiErrors(data, ['email', 'password', 'captcha']);
          return;
        }

        setFieldErrors({
          ...initialErrors,
          email: data.message || data.error || 'Ошибка регистрации. Попробуйте позже.',
        });
        return;
      }

      const res = await fetch(`${base}/v1/auth/registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          smart_token: captchaToken,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && typeof data.id !== 'undefined') {
        setRegistrationId(data.id);
        setStep('code');
        setCode('');
        clearAllErrors();
        return;
      }

      if (res.status === 400) {
        applyApiErrors(data, ['email', 'password', 'captcha']);
        return;
      }

      setFieldErrors({
        ...initialErrors,
        email: data.message || 'Ошибка регистрации. Попробуйте позже.',
      });
    } catch (err) {
      setFieldErrors({
        ...initialErrors,
        email: 'Ошибка сети. Проверьте REACT_APP_FW_API и доступность API.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSecondStep = async () => {
    clearAllErrors();
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setFieldErrors({ ...initialErrors, code: 'Введите код из письма.' });
      return;
    }
    const base = getApiBase();
    if (!base) {
      setFieldErrors({ ...initialErrors, code: 'Не задан REACT_APP_FW_API.' });
      return;
    }
    if (!registrationId) {
      setFieldErrors({
        ...initialErrors,
        code: 'Не найден идентификатор регистрации. Попробуйте ещё раз начать регистрацию.',
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${base}/v1/auth/registration-validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: registrationId,
          smart_token: captchaToken,
          code: trimmedCode,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('login', JSON.stringify(true));
        navigate(`${process.env.PUBLIC_URL}/home`);
        return;
      }

      if (res.status === 400) {
        applyApiErrors(data, ['code']);
        return;
      }

      setFieldErrors({
        ...initialErrors,
        code: data.message || 'Ошибка проверки кода. Попробуйте позже.',
      });
    } catch (err) {
      setFieldErrors({
        ...initialErrors,
        code: 'Ошибка сети. Проверьте REACT_APP_FW_API и доступность API.',
      });
    } finally {
      setLoading(false);
    }
  };

  const hasEmailError = Boolean(fieldErrors.email);
  const hasPasswordError = Boolean(fieldErrors.password);
  const hasPasswordConfirmError = Boolean(fieldErrors.passwordConfirm);
  const hasCodeError = Boolean(fieldErrors.code);

  return (
    <Form className="theme-form needs-validation" noValidate onSubmit={handleSubmit}>
      <H4>Регистрация</H4>
      {step === 'form' && <P>Введите email и пароль для регистрации</P>}
      {step === 'code' && <P>Введите код из письма, отправленный на {email}</P>}

      {step === 'form' && (
        <>
          <FormGroup>
            <Label className="col-form-label">{EmailAddress}</Label>
            <Input
              className={hasEmailError ? 'form-control is-invalid' : 'form-control'}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError('email');
              }}
              placeholder="email@example.com"
            />
            {hasEmailError && <div className="invalid-feedback d-block">{fieldErrors.email}</div>}
          </FormGroup>

          <FormGroup className="position-relative">
            <Label className="col-form-label">{Password}</Label>
            <div className="position-relative">
              <Input
                className={hasPasswordError ? 'form-control is-invalid' : 'form-control'}
                type={togglePassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearFieldError('password');
                }}
              />
            </div>
            {hasPasswordError && (
              <div className="invalid-feedback d-block">{fieldErrors.password}</div>
            )}
          </FormGroup>

          <FormGroup className="position-relative">
            <Label className="col-form-label">Повторите пароль</Label>
            <div className="position-relative">
              <Input
                className={hasPasswordConfirmError ? 'form-control is-invalid' : 'form-control'}
                type={togglePassword ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => {
                  setPasswordConfirm(e.target.value);
                  clearFieldError('passwordConfirm');
                }}
              />
              <div className="show-hide" onClick={() => setTogglePassword(!togglePassword)}>
                <span className={togglePassword ? '' : 'show'}></span>
              </div>
            </div>
            {hasPasswordConfirmError && (
              <div className="invalid-feedback d-block">{fieldErrors.passwordConfirm}</div>
            )}
          </FormGroup>

          <div className="mt-2">
            {CAPTCHA_KEY ? (
              <SmartCaptcha
                sitekey={CAPTCHA_KEY}
                language="ru"
                theme="light"
                onSuccess={(token) => {
                  setCaptchaToken(token);
                  clearFieldError('captcha');
                }}
                onError={() => {
                  setCaptchaToken('');
                  setFieldErrors((prev) => ({
                    ...prev,
                    captcha: 'Ошибка капчи. Попробуйте ещё раз.',
                  }));
                }}
              />
            ) : (
              <div className="fw-auth-note">
                Регистрация временно недоступна — на сервере не настроена
                проверка «я не робот». Мы уже занимаемся этим; если нужно
                срочно, напишите в поддержку.
              </div>
            )}
            {fieldErrors.captcha && (
              <div className="invalid-feedback d-block mt-2">{fieldErrors.captcha}</div>
            )}
          </div>
        </>
      )}

      {step === 'code' && (
        <FormGroup className="position-relative">
          <Label className="col-form-label">Введите код из письма</Label>
          <Input
            className={hasCodeError ? 'form-control is-invalid' : 'form-control'}
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              clearFieldError('code');
            }}
            placeholder="Код из письма"
          />
          {hasCodeError && <div className="invalid-feedback d-block">{fieldErrors.code}</div>}
        </FormGroup>
      )}

      <div style={{ marginTop: '20px' }}>
        <Btn
          attrBtn={{
            color: 'primary',
            className: 'd-block w-100',
            type: 'submit',
            disabled: loading,
          }}
        >
          {loading
            ? (step === 'form' ? 'Регистрация...' : 'Проверка...')
            : (step === 'form' ? 'Регистрация' : 'Проверить код')}
        </Btn>
      </div>
    </Form>
  );
};

export default RegisterTab;
